import type { BasketItem, BasketLineItem } from "~~/models/baskets";
import type { BasketValidationResponse } from "~~/server/api/ecom/baskets/[basketid]/validation.post";
import type { User } from "#auth-utils";
import { NatchOSPlanktonImplementationModelsBasketStatus } from "~~/server/plankton/schema";
import { useCurrentBasket } from "./states";

export async function useBasket(
  user?: User | null | undefined,
  options: {
    initialize: boolean;
    setStatus: boolean;
  } = { initialize: false, setStatus: false },
) {
  const { trackBeginCheckout } = useGtmTracking();
  const { t } = useI18n();
  const { path } = useRoute();

  const stateKey = user?.customerId ? `c:${user.customerId}` : `guest`;
  const fetchKey = user?.customerId ? `c:${user.customerId}-${path}` : `guest-${path}`;

  const currentBasket = useCurrentBasket(stateKey);

  const updateStatus = async (newStatus: NatchOSPlanktonImplementationModelsBasketStatus) => {
    if (!currentBasket.value?.id || currentBasket.value.status === newStatus) {
      return;
    }

    try {
      await $fetch(`/api/ecom/baskets/${currentBasket.value.id}/status`, {
        method: "PUT",
        body: {
          status: newStatus,
        },
      });

      currentBasket.value.status = newStatus;
    }
    catch (error) {
      console.error("Error updating basket status:", error);
      useFailToast(t("common.title.error"), createFetchErrorMessage(error));
    }
  };

  const { status, execute, error } = useFetch("/api/ecom/baskets/current", {
    key: fetchKey,
    // server: false,
    method: "GET",
    lazy: true,
    immediate: options.initialize,
    dedupe: "defer",
    watch: false,
    deep: false,
    onRequest: ({ request }) => {
      // eslint-disable-next-line no-console
      console.log(`fetch (request: ${JSON.stringify(request)}`);
    },
    onResponse: async ({ response }) => {
      const responseData = response._data as BasketItem;
      // console.log("fetch current basket response", responseData);

      currentBasket.value = responseData;

      if (options.setStatus) {
        if (path.endsWith("/checkout/review")) {
          await updateStatus(NatchOSPlanktonImplementationModelsBasketStatus.CheckoutReview);
        }
        else if (path.endsWith("/checkout/information")) {
          await updateStatus(NatchOSPlanktonImplementationModelsBasketStatus.CheckoutDetails);
        }
        if (path.endsWith("/checkout")) {
          await updateStatus(NatchOSPlanktonImplementationModelsBasketStatus.CheckoutContent);

          if (currentBasket.value.lines?.length) {
            const dataLayerList = currentBasket.value.lines
              .filter((l: BasketLineItem) => l.product !== null && l.product !== undefined)
              .map((l: BasketLineItem) => l.product!.dataLayer);

            trackBeginCheckout(dataLayerList, currentBasket.value.totalLinesNetVatExcluded);
          }
        }
      }
    },
    onResponseError: (error) => {
      console.error("Could not get current basket", error.response);
    },
  });

  const validate = async () => {
    if (!currentBasket.value) {
      return {
        hasUpdate: {
          overpacks: false,
          prices: false,
          removals: false,
          replacements: false,
          transferDate: false,
        },
        overpacks: [],
        prices: [],
        removals: [],
        replacements: [],
        trackData: {
          addedProducts: [],
          removedProducts: [],
        },
      } satisfies BasketValidationResponse;
    }

    return $fetch<BasketValidationResponse>(`/api/ecom/baskets/${currentBasket.value.id}/validation`, {
      method: "POST",
    });
  };

  return {
    status: status,
    error: error,
    basket: currentBasket,
    execute: execute,
    validate: validate,
  };
}
